import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid, valueOrDefault } from '@web/utils';
import { PublicCartCreateResponse } from '../response/public-cart-create.response';
import { PublicCartDetailsResponse } from '../response/public-cart-details.response';
import { PublicCartAddItemRequest } from '../requests/public-cart-add-item.request';
import { PublicCartItemCollectionItemResponse } from '../response/public-cart-item-collection-item.response';
import { PublicCartUpdateItemRequest } from '../requests/public-cart-update-item.request';
import { PublicCartId } from '../../types/public-cart-id';
import { PublicCartAttributeRequest } from '../requests/public-cart-attribute.request';
import { ShippingMethodResponse } from '../../../shared/api/responses/shipping-method.response';
import { PublicCartPaymentMethodResponse } from '../response/public-cart-payment-method.response';
import { PublicCartCheckoutRequest } from '../requests/public-cart-checkout.request';
import { PublicCartCheckoutResponse } from '../response/public-cart-checkout.response';

@Injectable({
  providedIn: 'root',
})
export class PublicCartApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  /* prettier-ignore */
  public create(): Observable<PublicCartCreateResponse> {
    const path = `cart`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http
      .post(url, body, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(PublicCartCreateResponse)));
  }

  /* prettier-ignore */
  public addItem(options: {
    cartId: PublicCartId;
    request: PublicCartAddItemRequest;
  }): Observable<PublicCartItemCollectionItemResponse> {
    const path = `cart/${HttpParams.CartId}/item`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {[HttpParams.CartId]: options.cartId});
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(options.request, PublicCartAddItemRequest);

    return this.provider.http.post(url, body, { context }).pipe(map(this.provider.httpHelper.bodyToResponse(PublicCartItemCollectionItemResponse)));
  }

  public updateItem(options: {
    cartId: PublicCartId;
    itemId: Guid;
    request: PublicCartUpdateItemRequest;
  }): Observable<void> {
    const path = `cart/${HttpParams.CartId}/item/${HttpParams.ItemId}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.CartId]: options.cartId,
      [HttpParams.ItemId]: options.itemId,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      options.request,
      PublicCartUpdateItemRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public removeItem(options: {
    cartId: PublicCartId;
    itemId: string;
  }): Observable<void> {
    const path = `cart/${HttpParams.CartId}/item/${HttpParams.ItemId}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.CartId]: options.cartId,
      [HttpParams.ItemId]: options.itemId,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http.delete(url, { context }).pipe(blank());
  }

  /* prettier-ignore */
  public fetchDetailsById(options: {
    cacheable?: boolean;
    cartId: PublicCartId;
  }): Observable<PublicCartDetailsResponse> {
    const path = `cart/${HttpParams.CartId}`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({ [HttpParams.CartId]: options.cartId })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => valueOrDefault(options?.cacheable, false))
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(PublicCartDetailsResponse)));
  }

  /* prettier-ignore */
  public fetchShippingMethodsById(options: {
    cacheable?: boolean;
    cartId: PublicCartId;
    request: CollectionRequest;
  }): Observable<CollectionResponse<ShippingMethodResponse>> {
    const { cartId, request } = options;
    const path = `cart/${HttpParams.CartId}/shipping/method`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({ [HttpParams.CartId]: cartId })
        .queryParams(this.provider.httpHelper.collectionRequestToQueryParams(request))
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => valueOrDefault(options?.cacheable, false))
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToCollectionResponse(ShippingMethodResponse)));
  }

  /* prettier-ignore */
  public fetchPaymentMethodsById(options: {
    cacheable?: boolean;
    cartId: PublicCartId;
    request: CollectionRequest;
  }): Observable<CollectionResponse<PublicCartPaymentMethodResponse>> {
    const { cartId, request } = options;
    const path = `cart/${HttpParams.CartId}/payment/method`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({ [HttpParams.CartId]: cartId })
        .queryParams(this.provider.httpHelper.collectionRequestToQueryParams(request))
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => valueOrDefault(options?.cacheable, false))
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToCollectionResponse(PublicCartPaymentMethodResponse)));
  }

  /* prettier-ignore */
  public fetchCollectionById(options: {
    cartId: PublicCartId;
    request: CollectionRequest;
    cacheable?: boolean;
  }): Observable<
    CollectionResponse<PublicCartItemCollectionItemResponse>
  > {
    const { cartId, request } = options;
    const path = `cart/${HttpParams.CartId}/item`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({ [HttpParams.CartId]: cartId })
        .queryParams(this.provider.httpHelper.collectionRequestToQueryParams(request))
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => valueOrDefault(options?.cacheable, false))
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToCollectionResponse(PublicCartItemCollectionItemResponse)));
  }

  /* prettier-ignore */
  public updateAttributes(options: {
    cartId: PublicCartId;
    request: PublicCartAttributeRequest;
  }): Observable<void> {
    const { cartId, request } = options;
    const path = `cart/${HttpParams.CartId}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.CartId]: cartId });
    const body = this.provider.httpHelper.requestToBody(request, PublicCartAttributeRequest);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http.patch(url, body, { context }).pipe(blank());
  }

  public checkout(options: {
    cartId: PublicCartId;
    request: PublicCartCheckoutRequest;
  }): Observable<PublicCartCheckoutResponse> {
    const { cartId, request } = options;
    const path = `cart/${cartId}/checkout`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      PublicCartCheckoutRequest,
    );

    return this.provider.http
      .post(url, body, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(PublicCartCheckoutResponse),
        ),
      );
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
